"use client";

import { Home, User, Heart, UserPlus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { CgDetailsMore } from "react-icons/cg";
import { SlUserFollow } from "react-icons/sl";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Truck,
  Building2,
  Store,
  Wrench,
  Briefcase,
  Package,
  MessageCircle,
} from "lucide-react";
import { useSocket } from "../context/SocketContext";

const categories = [
  {
    id: "all",
    name: "Products",
    icon: Package,
    count: "500K+",
    link: "/products",
  },
  {
    id: "sellers",
    name: "Sellers",
    icon: Building2,
    count: "25K+",
    link: "/sellers",
  },
  {
    id: "logistics",
    name: "Logistics",
    icon: Truck,
    count: "15K+",
    link: "/logistics",
  },
  {
    id: "services",
    name: "Services",
    icon: Wrench,
    count: "30K+",
    link: "/services",
  },
  {
    id: "franchise",
    name: "Franchise",
    icon: Store,
    count: "5K+",
    link: "/franchise",
  },
  {
    id: "business",
    name: "Business",
    icon: Briefcase,
    count: "10K+",
    link: "/business",
  },
  {
    id: "merzado services",
    name: "Merzado Services",
    icon: Briefcase,
    count: "10K+",
    link: "/ourservices",
  },
];

const SERVICES_THEME = {
  background: "bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500",
  accent: "text-indigo-100",
  border: "border-indigo-500/30",
  hover: "hover:text-indigo-200",
};

const LOGISTICS_THEME = {
  background: "bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500",
  accent: "text-teal-100",
  border: "border-teal-500/30",
  hover: "hover:text-teal-200",
};

const BUSINESS_THEME = {
  background: "bg-gradient-to-r from-emerald-600 to-teal-600",
  accent: "text-green-100",
  border: "border-green-500/30",
  hover: "hover:text-green-200",
};

const PRODUCTS_THEME = {
  background: "bg-gradient-to-r from-blue-600 to-blue-800",
  accent: "text-blue-100",
  border: "border-blue-500/30",
  hover: "hover:text-blue-200",
};

const FRANCHISE_THEME = {
  background: "bg-gradient-to-r from-indigo-600 to-purple-600",
  accent: "text-purple-100",
  border: "border-purple-500/30",
  hover: "hover:text-purple-200",
};

const SELLER_THEME = {
  background: "bg-gradient-to-r from-blue-600 to-blue-800",
  accent: "text-blue-100",
  border: "border-blue-500/30",
  hover: "hover:text-blue-200",
};

// Page color themes (same as navbar)
const PAGE_THEMES = {
  "/": {
    background: "bg-gradient-to-r from-blue-600 to-blue-800",
    accent: "text-blue-100",
    border: "border-blue-500/30",
    hover: "hover:text-blue-200",
  },

  "/local-suppliers": {
    background: "bg-gradient-to-r from-orange-500 to-orange-700",
    accent: "text-orange-100",
    border: "border-orange-400/30",
    hover: "hover:text-orange-200",
  },

  "/products": PRODUCTS_THEME,
  // "/products/:productid": PRODUCTS_THEME,

  "/sellers": SELLER_THEME,
  // "/sellers/:sellerId": SELLER_THEME,

  "/franchise": FRANCHISE_THEME,
  // "/franchise/:franchiseId": FRANCHISE_THEME,

  "/business": BUSINESS_THEME,
  // "/business/:id": BUSINESS_THEME,

  "/logistics": LOGISTICS_THEME,
  // "/logistics/:id": LOGISTICS_THEME,

  "/services": SERVICES_THEME,
  // "/services/:id": SERVICES_THEME,

  default: {
    background: "bg-gradient-to-r from-blue-600 to-blue-800",
    accent: "text-blue-100",
    border: "border-blue-500/30",
    hover: "hover:text-blue-200",
  },
};

export default function MobileVersionFooter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [showCategoriesPopup, setShowCategoriesPopup] = useState(false);
  const [showFollowPopup, setShowFollowPopup] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("isLoggedIn") === "true"
  );
  const { unreadCount } = useSocket() || {};

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // Update login status when localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem("isLoggedIn") === "true";
      setIsLoggedIn(loginStatus);
    };

    // Check initially
    checkLoginStatus();

    // Listen for storage changes (from other tabs)
    window.addEventListener("storage", checkLoginStatus);

    // Also check periodically for same-tab changes
    const interval = setInterval(checkLoginStatus, 1000);

    return () => {
      window.removeEventListener("storage", checkLoginStatus);
      clearInterval(interval);
    };
  }, []);

  // Update login status when localStorage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = localStorage.getItem("isLoggedIn") === "true";
      setIsLoggedIn(loginStatus);
    };

    // Check initially
    checkLoginStatus();

    // Listen for storage changes
    window.addEventListener("storage", checkLoginStatus);

    // Also check periodically in case of same-tab changes
    const interval = setInterval(checkLoginStatus, 1000);

    return () => {
      window.removeEventListener("storage", checkLoginStatus);
      clearInterval(interval);
    };
  }, []);

  // Check login status on mount
  // useEffect(() => {
  //   axios
  //     .get(`${process.env.REACT_APP_BASE_URL}/currentuser`, { withCredentials: true })
  //     .then((response) => {
  //       if (response.status === 200 && response.data.user) {
  //         setIsLoggedIn(true)
  //       } else {
  //         setIsLoggedIn(false)
  //       }
  //     })
  //     .catch(() => setIsLoggedIn(false))
  // }, [])

  const getCurrentTheme = () => {
    // Exact match first
    if (PAGE_THEMES[currentPath]) return PAGE_THEMES[currentPath];

    // Dynamic route matching
    if (currentPath.startsWith("/products/"))
      return PAGE_THEMES["/products/:productid"];
    if (currentPath.startsWith("/sellers/"))
      return PAGE_THEMES["/sellers/:sellerId"];
    if (currentPath.startsWith("/franchise/"))
      return PAGE_THEMES["/franchise/:franchiseId"];
    if (currentPath.startsWith("/business/"))
      return PAGE_THEMES["/business/:id"];
    if (currentPath.startsWith("/logistics/"))
      return PAGE_THEMES["/logistics/:id"];
    if (currentPath.startsWith("/services/"))
      return PAGE_THEMES["/services/:id"];

    // Fallback
    return PAGE_THEMES.default;
  };

  const navigateTo = (path) => {
    window.location.href = path;
  };

  const theme = getCurrentTheme();

  const toggleCategoriesPopup = (e) => {
    e.stopPropagation();
    setShowCategoriesPopup((prev) => !prev);
    setShowFollowPopup(false); // Close follow popup if open
  };

  const toggleFollowPopup = (e) => {
    e.stopPropagation();
    setShowFollowPopup((prev) => !prev);
    setShowCategoriesPopup(false); // Close categories popup if open
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showCategoriesPopup) setShowCategoriesPopup(false);
      if (showFollowPopup) setShowFollowPopup(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showCategoriesPopup, showFollowPopup]);

  const handleScanQR = () => {
    console.log("Opening QR scanner");
    // In a real app, you would request camera access and open a QR scanner
    alert("Opening QR scanner camera");
    // You could navigate to a scanner page
    // navigate("/qr-scanner");
  };

  const handlePaymentOptions = () => {
    console.log("Opening payment options");
    // Navigate to payment options page
    navigate("/payments");
    setShowCategoriesPopup(false);
  };

  const handleEntertainment = () => {
    console.log("Opening entertainment options");
    // Navigate to entertainment page
    navigate("/entertainment");
    setShowCategoriesPopup(false);
  };

  // Add this function to determine if a path is active
  const isActive = (path) => {
    // For home path
    if (path === "/" && currentPath === "/") {
      return true;
    }
    // For other paths, check if currentPath starts with the path
    // This handles nested routes like /MessagesInboxes/123
    return path !== "/" && currentPath.startsWith(path);
  };

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // ✅ PLACE THIS HERE - INSIDE THE FUNCTION BODY
  const HIDDEN_PATHS = [
    /^\/franchise\/[^/]+$/,
    /^\/services\/[^/]+$/,
    /^\/sellers\/[^/]+$/,
    /^\/products\/[^/]+$/,
    /^\/logistics\/[^/]+$/,
    /^\/business\/[^/]+$/,
    /^\/products\/type\/[^/]+$/, // /products/type/:producttype
    /^\/products\/category\/[^/]+$/, // /products/category/:subcategory

    /^\/livestream\/start$/,
  ];
  const shouldHideFooter = HIDDEN_PATHS.some((regex) =>
    regex.test(currentPath)
  );
  if (shouldHideFooter) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 md:hidden z-50 w-full">
      {/* Categories Popup */}
      {showCategoriesPopup && (
        <div
          className={`bg-white shadow-lg pt-4 pb-0 mb-2 w-full relative transform transition-all duration-700 ease-in-out
    ${showCategoriesPopup
              ? "translate-y-0 opacity-100"
              : "translate-y-full opacity-0 pointer-events-none"
            }
  `}
        >
          <div className="max-w-5xl mx-auto ">
            <div className="bg-gray-100 p-3 space-y-3">
              {/* First 6 categories in 3-column grid */}
              <div className="grid grid-cols-3 gap-3">
                {categories.slice(0, 6).map((category, index) => {
                  const IconComponent = category.icon;

                  return (
                    <div
                      key={category.id}
                      onClick={() => {
                        setActiveCategory(category.id);
                        navigateTo(category.link);
                      }}
                      className={`cursor-pointer rounded-xl text-center transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 active:translate-y-1 ${activeCategory === category.id
                        ? "bg-[#2563eb] text-white scale-105 shadow-lg"
                        : "bg-white/90 text-gray-800 hover:bg-gray-50 hover:shadow-md"
                        } h-20 flex flex-col items-center justify-center p-2`}
                    >
                      <div className="flex flex-col items-center h-full justify-center">
                        <IconComponent
                          className={`h-5 w-5 mb-1 transition-colors duration-200 ${activeCategory === category.id
                            ? "text-white"
                            : "text-gray-600"
                            }`}
                        />
                        <h3
                          className={`text-[10px] font-medium transition-colors duration-200 ${activeCategory === category.id
                            ? "text-white"
                            : "text-gray-600"
                            } text-center leading-tight`}
                        >
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Join Merzado and Merzado Services side by side */}
              <div className="flex justify-center gap-2 mt-3">
                {/* Join Merzado Button */}
                <div
                  onClick={() => {
                    navigate("/joinmerzado");
                    setShowCategoriesPopup(false);
                  }}
                  className="cursor-pointer rounded-lg text-center transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 active:translate-y-1 bg-gradient-to-r from-pink-500 to-yellow-500 text-white hover:from-pink-600 hover:to-yellow-600 shadow-lg h-14 w-24 flex flex-col items-center justify-center p-2"
                >
                  <div className="flex flex-col items-center h-full justify-center">
                    <UserPlus className="h-4 w-4 mb-1 text-white" />
                    <span className="text-[9px] font-medium text-white text-center leading-tight">
                      Join Merzado
                    </span>
                  </div>
                </div>

                {/* Merzado Services */}
                {categories.slice(6).map((category, index) => {
                  const IconComponent = category.icon;

                  return (
                    <div
                      key={category.id}
                      onClick={() => {
                        setActiveCategory(category.id);
                        navigateTo(category.link);
                      }}
                      className={`cursor-pointer rounded-lg text-center transition-all duration-300 ease-in-out transform hover:scale-105 active:scale-95 active:translate-y-1 ${activeCategory === category.id
                        ? "bg-[#2563eb] text-white scale-105 shadow-lg"
                        : "bg-white/90 text-gray-800 hover:bg-gray-50 hover:shadow-md"
                        } h-14 w-24 flex flex-col items-center justify-center p-2`}
                    >
                      <div className="flex flex-col items-center h-full justify-center">
                        <IconComponent
                          className={`h-4 w-4 mb-1 transition-colors duration-200 ${activeCategory === category.id
                            ? "text-white"
                            : "text-gray-600"
                            }`}
                        />
                        <h3
                          className={`text-[9px] font-medium transition-colors duration-200 ${activeCategory === category.id
                            ? "text-white"
                            : "text-gray-600"
                            } text-center leading-tight`}
                        >
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow Popup */}
      {showFollowPopup && (
        <div
          className={`bg-white shadow-lg pt-4 pb-4 mb-2 w-full relative transform transition-all duration-700 ease-in-out
    ${showFollowPopup
              ? "translate-y-0 opacity-100"
              : "translate-y-full opacity-0 pointer-events-none"
            }
  `}
        >
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Wishlist Button */}
              <button
                onClick={() => {
                  navigateTo("/wishlist");
                  setShowFollowPopup(false);
                }}
                className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
              >
                <Heart className="h-5 w-5 text-gray-600" />
                <span className="text-gray-800 font-medium">Wishlist</span>
              </button>

              {/* Follow Button */}
              <button
                onClick={() => {
                  navigateTo("/Following");
                  setShowFollowPopup(false);
                }}
                className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 py-3 rounded-lg transition-colors duration-150"
              >
                <SlUserFollow className="h-5 w-5 text-gray-600" />
                <span className="text-gray-800 font-medium">Follow</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white px-4 py-3 w-full h-[70px] border-t border-gray-200 shadow-sm pb-safe">
        <div className="flex w-full justify-between items-center h-full pb-2">
          {/* Home */}
          <div
            className="flex flex-col items-center space-y-1 opacity-100"
            onClick={() => navigate("/")}
          >
            <div
              className={`rounded-full p-1 transition-all duration-200 ${isActive("/") ? "bg-gray-200" : ""
                }`}
            >
              <Home
                className={`w-5 h-5 text-gray-600 stroke-2 ${isActive("/") ? "stroke-[2.5]" : ""
                  }`}
              />
            </div>
            <span className="text-gray-600 text-[11px] font-medium">Home</span>
          </div>

          {/* Messages */}
          <div
            className="flex flex-col items-center space-y-1 opacity-100 cursor-pointer"
            onClick={() => {
              console.log("Messages button clicked"); // Debug log
              console.log("isLoggedIn:", isLoggedIn);
              console.log(
                "localStorage isLoggedIn:",
                localStorage.getItem("isLoggedIn")
              );
              if (!isLoggedIn) {
                console.log("Navigating to login from Messages");
                navigate("/login");
              } else {
                const role = localStorage.getItem("role");
                let dashboardPath = "/userdashboard";
                if (role === "SELLER") dashboardPath = "/sellerdashboard";
                if (role === "FRANCHISE")
                  dashboardPath = "/dashboard/franchise";
                if (role === "BUSINESS")
                  dashboardPath = "/dashboard/business-services";
                if (role === "SERVICE") dashboardPath = "/dashboard/services";
                if (role === "LOGISTICS") dashboardPath = "/logisticsDashboard";

                // Force refresh and navigate to chat
                window.location.href = `${dashboardPath}?section=chat`;
              }
            }}
          >
            <div
              className={`rounded-full p-1 transition-all duration-200 ${isActive("/chatcomp") ? "bg-gray-200" : ""
                } relative`}
            >
              <MessageCircle
                className={`w-5 h-5 text-gray-600 stroke-2 ${isActive("/chatcomp") ? "stroke-[2.5]" : ""
                  }`}
              />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            <span className="text-gray-600 text-[11px] font-medium">
              Messages
            </span>
          </div>

          {/* Middle Category - Services */}
          <div
            className="flex flex-col items-center space-y-1 opacity-100"
            onClick={toggleCategoriesPopup}
          >
            <div
              className={`rounded-full p-1 transition-all duration-200 ${showCategoriesPopup ? "bg-gray-200" : ""
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`text-gray-600 ${showCategoriesPopup ? "stroke-[2.5]" : ""
                  }`}
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <span className="text-gray-600 text-[11px] font-medium">
              Categories
            </span>
          </div>

          {/* Follow - Now opens popup */}
          <div
            className="flex flex-col items-center opacity-100"
            onClick={toggleFollowPopup}
          >
            <div
              className={`rounded-full p-1 transition-all duration-200 ${showFollowPopup ? "bg-gray-200" : ""
                }`}
            >
              <CgDetailsMore className={`w-5 h-5 text-gray-600`} />
            </div>
            <span className="text-[11px] font-medium text-gray-600">More</span>
          </div>

          {/* Account/Login */}
          {/* Account/Login */}
          {!isLoggedIn ? (
            <div
              className="flex flex-col items-center space-y-1 opacity-100 cursor-pointer"
              onClick={() => {
                console.log("Login button clicked");
                navigate("/login");
              }}
            >
              <div
                className={`rounded-full p-1 transition-all duration-200 ${isActive("/userdashboard") ? "bg-gray-200" : ""
                  }`}
              >
                <User
                  className={`w-5 h-5 text-gray-600 stroke-2 ${isActive("/userdashboard") ? "stroke-[2.5]" : ""
                    }`}
                />
              </div>
              <span className="text-gray-600 text-[11px] font-medium">Login</span>
            </div>
          ) : (
            <div
              className="flex flex-col items-center space-y-1 cursor-pointer"
              onClick={() => {
                console.log("Account button clicked when logged in");
                console.log("isLoggedIn:", isLoggedIn);

                const role = (localStorage.getItem("role") || "").toUpperCase();
                console.log("User role:", role);

                if (!role) {
                  // 🚨 No role found → treat as not logged in
                  navigate("/login");
                  return;
                }

                if (role === "USER") {
                  window.location.href = "/userdashboard";
                } else if (role === "SELLER") {
                  window.location.href = "/sellerdashboard";
                } else if (role === "FRANCHISE") {
                  window.location.href = "/dashboard/franchise";
                } else if (role === "SERVICE") {
                  window.location.href = "/dashboard/services";
                } else if (role === "LOGISTICS") {
                  window.location.href = "/logisticsDashboard";
                } else if (role === "BUSINESS") {
                  window.location.href = "/dashboard/business-services";
                } else {
                  // 🚨 Unknown role → send to login
                  window.location.href = "/login";
                }
              }}
            >
              <div
                className={`rounded-full p-1 transition-all duration-200 ${isActive("/userdashboard") ? "bg-gray-200" : ""
                  }`}
              >
                <User
                  className={`w-5 h-5 text-gray-600 stroke-2 ${isActive("/userdashboard") ? "stroke-[2.5]" : ""
                    }`}
                />
              </div>
              <span className="text-gray-600 text-[11px] font-medium">Account</span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}